import ModalHeader from '@/components/shared/modals/components/ModalHeader/ModalHeader';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import useSWRInfinite from 'swr/infinite';
import styles from './ModalPressKit.module.scss';
import { serializeCollectionResource } from '@/utils/serilize-resource';
import { useClient } from '@/components/shared/utilities/AppProvider/AppProvider';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import Gallery from '@/components/shared/gallery/Gallery';
import { IModalComponentProps } from '@/components/shared/modals/Modals.interfaces';
import Tabs from '@/components/shared/tabs/Tabs';
import TabLink from '@/components/shared/tabs/TabLink';
import TabContent from '@/components/shared/tabs/TabContent';
import tabStyles from '@/assets/stylesheets/components/_tabs.module.scss';
import classNames from 'classnames';

export enum MuseumLocationFilter {
	ZAGREB = 'zagreb',
	CHIANG_MAI = 'chiang_mai',
	GENERAL = 'general',
}

export const ModalPressKit: FC<IModalComponentProps> = ({ onClose }) => {
	const { t } = useTranslation();
	const client = useClient();

	const [selectedTab, setSelectedTab] = useState(MuseumLocationFilter.GENERAL);

	const {
		data: pressKit,
		error,
		setSize,
		size,
	} = useSWRInfinite(
		(pageIndex, previousPageData) => {
			if (previousPageData && !previousPageData?.links?.next) return null; // reached the end

			const pageNumber = pageIndex + 1;

			return [
				'press',
				{
					page: pageNumber,
					per_page: 16,
					filters: 'press_kit',
					filter: {
						location: selectedTab,
					},
				},
			] as const;
		},
		async (key) => {
			const data = await client.findAll(...key);

			return serializeCollectionResource(data);
		}
	);

	const { items, isLoadingMore, isReachingEnd } = useMemo(
		() => extractSWRInfiniteData(pressKit, error, size),
		[pressKit, error, size]
	);

	const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

	const tabClassName = classNames(tabStyles.tabLink, styles.tabItem);

	return (
		<div className={styles.modal}>
			<ModalHeader title={t('press-kit')} close={onClose} />
			<div className={styles.kitSubtitle}>{t('please-credit-the-photos-to-th')}</div>

			<Tabs
				name="museum-locations"
				handleSelect={(selectedTab) => setSelectedTab(selectedTab)}
				selectedTab={selectedTab}
			>
				<div className={styles.tabs}>
					<TabLink
						tabTo={MuseumLocationFilter.GENERAL}
						className={tabClassName}
						activeClassName={tabStyles.tabLinkActive}
					>
						General
					</TabLink>
					<TabLink
						tabTo={MuseumLocationFilter.ZAGREB}
						className={tabClassName}
						activeClassName={tabStyles.tabLinkActive}
					>
						Zagreb
					</TabLink>
					<TabLink
						tabTo={MuseumLocationFilter.CHIANG_MAI}
						className={tabClassName}
						activeClassName={tabStyles.tabLinkActive}
					>
						Chiang Mai
					</TabLink>
				</div>

				<TabContent contentFor={selectedTab}>
					<Gallery
						className={styles.contentSmallBottom}
						collection={items}
						onLoadMore={onLoadMore}
						hasMore={!isReachingEnd}
						loading={isLoadingMore}
					/>
				</TabContent>
			</Tabs>
		</div>
	);
};
